import type { AnnotationInfo } from '@atlaskit/editor-plugins/annotation';

import { CommentCreationLocation } from '@confluence/inline-comments-queries/entry-points/CreateInlineCommentMutation.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import {
	REPLY_TO_INLINE_COMMENT_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { InlineCommentFramework } from '@confluence/inline-comments-utils/entry-points/enum.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { CommentType } from '@confluence/comments-util/entry-points/analytics.ts';

import { getTargetNodeType } from './analyticsUtils.ts';

export type DefaultReactionsSummary = {
	ari: string;
	containerAri: string;
	reactionsCount: number;
	reactionsSummaryForEmoji: any[];
	__typename: 'ReactionsSummaryResponse';
};

export const getCommentCreationLocation = (
	isEditor: boolean,
	isLivePage: boolean,
):
	| CommentCreationLocation.EDITOR
	| CommentCreationLocation.LIVE
	| CommentCreationLocation.RENDERER => {
	if (isLivePage) return CommentCreationLocation.LIVE;
	if (isEditor) return CommentCreationLocation.EDITOR;

	return CommentCreationLocation.RENDERER;
};

interface HandleCreateInlineReplySuccessProps {
	onSuccess: (() => void) | undefined;
	editCommentQueryId: string | undefined;
	setCommentForEdit: any;
	data: any;
	createAnalyticsEvent: any;
	pageId: string | undefined;
	pageType: string;
	analyticsSource: AnalyticsSource;
	commentId: string | undefined;
	pageMode: PageMode;
	selectedAnnotation: AnnotationInfo | undefined;
	getInlineNodeTypes: any;
	experienceTracker: ExperienceTrackerAPI;
	isEditor: boolean;
	/** The parent thread's highlighted text has been deleted from the page. */
	isDangling?: boolean;
}

export const handleCreateReplySuccess = ({
	onSuccess,
	editCommentQueryId,
	setCommentForEdit,
	data,
	createAnalyticsEvent,
	pageId,
	pageType,
	analyticsSource,
	commentId,
	pageMode,
	selectedAnnotation,
	getInlineNodeTypes,
	experienceTracker,
	isEditor,
	isDangling,
}: HandleCreateInlineReplySuccessProps): void => {
	onSuccess?.();
	if (editCommentQueryId) {
		setCommentForEdit('');
	}

	const replyCommentInfo = data?.replyInlineComment;
	const markerRef = replyCommentInfo?.location?.inlineMarkerRef;

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'created',
			actionSubject: 'comment',
			actionSubjectId: replyCommentInfo?.id, // The newly created comment ID
			objectType: pageType,
			objectId: pageId,
			source: analyticsSource,
			attributes: {
				source: analyticsSource,
				commentType: CommentType.INLINE,
				parentCommentId: commentId ?? null, // analytics event schema type expects string or null
				isReply: true,
				pageMode,
				framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				navdexPointType: END,
				inlineNodeTypes:
					selectedAnnotation && getInlineNodeTypes
						? getInlineNodeTypes(selectedAnnotation.id)
						: undefined,
				targetNodeType: getTargetNodeType(markerRef, isEditor),
				// Whether the parent thread is detached. Not read from the response: a new reply's own
				// resolve properties come back with isDangling false regardless of its thread.
				isDangling: isDangling ?? false,
			},
		},
	}).fire();

	experienceTracker.updateAttributes({
		name: REPLY_TO_INLINE_COMMENT_EXPERIENCE,
		attributes: {
			replyCommentId: replyCommentInfo?.id,
		},
	});

	experienceTracker.succeed({
		name: REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
		attributes: {
			replyCommentId: replyCommentInfo?.id,
		},
	});
};

export const insertReopenedCommentAnnotation = (
	allAnnotations: string[],
	commentMarkerRef: string,
	previousOrderedActiveAnnotations: string[],
	setOrderedActiveAnnotationIdList: (annotations: string[]) => void,
): void => {
	const indexInAnnotations = allAnnotations.indexOf(commentMarkerRef);

	if (indexInAnnotations === -1) {
		throw new Error('Marker ref is not present in the annotations list');
	}

	const insertIndex = previousOrderedActiveAnnotations.findIndex(
		(annotation) => allAnnotations.indexOf(annotation) > indexInAnnotations,
	);

	const finalInsertIndex =
		insertIndex === -1 ? previousOrderedActiveAnnotations.length : insertIndex;

	const updatedOrderedActiveAnnotations = [
		...previousOrderedActiveAnnotations.slice(0, finalInsertIndex),
		commentMarkerRef,
		...previousOrderedActiveAnnotations.slice(finalInsertIndex),
	];
	setOrderedActiveAnnotationIdList(updatedOrderedActiveAnnotations);
};

export const inlineCommentExists = (inlineMarkerRef: string | null): boolean => {
	const tiny = `.inline-comment-marker[data-ref="${inlineMarkerRef}"]`;
	const fabricEditorAnnotation = `[id="${inlineMarkerRef}"][data-mark-annotation-type="inlineComment"]`;

	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	return Boolean(document.querySelector(`${tiny}, ${fabricEditorAnnotation}`));
};

export const defaultReactionsSummary: DefaultReactionsSummary = {
	ari: '',
	containerAri: '',
	reactionsCount: 0,
	reactionsSummaryForEmoji: [],
	__typename: 'ReactionsSummaryResponse',
};
